import React, { useEffect } from "react"
import { view } from "@risingstack/react-easy-state"
import { useHistory, Redirect } from "react-router-dom"
import styled from "styled-components"
import { EyeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons"
import {
  message,
  Button,
  Empty,
  Pagination,
  Table,
  List,
  Tooltip,
  Popconfirm,
} from "antd"

import StyledBox from "components/common/styled-components/StyledBox"
import StyledList from "components/common/styled-components/StyledList"
import { staleWhileRevalidate, networkOnly } from "utils/render-strategies"
import globalStore from "store/index"
import DocumentsService from "services/users/documents.service"

const StyledPageContent = styled.div`
  & > .action-buttons.top {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }
`

const DocumentIndexPage = (props) => {
  const history = useHistory()
  const pageState = globalStore.ui.documents.index

  useEffect(() => {
    const fetchDocuments = async () => {
      const pageState = globalStore.ui.documents.index
      await DocumentsService.index({ pageState })

      const { serverStatus, documentErrors } = pageState

      if (serverStatus.status !== 200) {
        message.error("Oops!! something went wrong")
        return
      }

      if (documentErrors) {
        message.error(documentErrors[0] || "Couldn't fetch document")
        return
      }
    }

    fetchDocuments()
  }, [])

  const repaginate = async (page, pageSize) => {
    await DocumentsService.index({ pageState, page, pageSize })
  }

  const handleCreate = () => {
    history.push(`/configuration/documents/new`)
  }

  const handleCardItemClick = (documentId) => {
    history.push(`/configuration/documents/${documentId}`)
  }

  const handleEdit = (documentId) => {
    history.push(`/configuration/documents/${documentId}/edit`)
  }

  const handleDelete = async (documentId) => {
    const tempState = {}

    await DocumentsService.delete({
      pageState: tempState,
      documentId,
    })

    const { serverStatus } = tempState

    if (serverStatus.status !== 200) return

    globalStore.ui.documents.index = {
      ...globalStore.ui.documents.index,
      documents: documents.filter((documents) => documents.id !== documentId),
    }
  }

  const {
    documents,
    documentsMeta,
    API_USERS_DOCUMENTS_INDEX_STATUS,
    displayType = "table",
  } = pageState

  const {
    total,
    page: currentPage,
    page_size: pageSize,
    criteria_keys,
  } = documentsMeta || {}

  const renderList = () => {
    return (
      <StyledList>
        {documents.map((ffc) => (
          <div
            className="item-card"
            key={`item-card-${ffc.id}`}
            onClick={() => handleCardItemClick(ffc.id)}
          >
            <div className="details-box">
              <div className="name-box">
                <div className="name">{ffc.name}</div>
                <div>{}</div>
              </div>
            </div>
            <div className="icon-box">
              <Button type="link">
                <EyeOutlined />
              </Button>
            </div>
          </div>
        ))}
      </StyledList>
    )
  }

  const renderContents = () => {
    if (documents.length === 0) {
      return (
        <Empty>
          <Button type="primary" ghost onClick={handleCreate}>
            Create Document
          </Button>
        </Empty>
      )
    }

    const pagination = (
      <Pagination
        current={currentPage}
        total={total}
        onChange={repaginate}
        onShowSizeChange={repaginate}
        pageSize={pageSize || 25}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    )

    const tableColumns = [
      ...[
        {
          title: "Name",
          dataIndex: "name",
          key: "name",
        },
        ...criteria_keys.map((ck) => ({
          title: ck,
          dataIndex: ck,
          key: ck,
          render: (filteredCriteria, row) => {

            if (!filteredCriteria) return "--" // if null
            if (!filteredCriteria[0]) return "--" // If [null]
            if (filteredCriteria.length === 0) return "---" // if []

            const dataSource = filteredCriteria.filter((fc) => fc)

            return (
              <List
                dataSource={dataSource}
                renderItem={(values) => {
                  return values.join(", ")
                }}
              ></List>
            )
          },
        })),
        {
          title: "Action",
          dataIndex: "action",
          key: "action",
          align: "center",
          render: (value, ffc) => {
            return (
              <div>
                <Tooltip title="View">
                  <Button
                    type="link"
                    onClick={() => handleCardItemClick(ffc.id)}
                  >
                    <EyeOutlined />
                  </Button>
                </Tooltip>
                <Tooltip title="Edit">
                  <Button type="link" onClick={() => handleEdit(ffc.id)}>
                    <EditOutlined />
                  </Button>
                </Tooltip>
                <Tooltip title="Delete">
                  <Popconfirm
                    title="Are you sure to delete?"
                    onConfirm={() => handleDelete(ffc.id)}
                    onCancel={() => {}}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link">
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                </Tooltip>
              </div>
            )
          },
        },
      ],
    ]

    const dataSource = documents.map((ffc) => {
      const retval = { ...ffc, key: ffc.id }
      criteria_keys.forEach((ck) => {
        retval[ck] = ffc.criteria.map((conditions) => {
          const relevantCondition = conditions.find((c) => c.field === ck)
          if (!relevantCondition) return null
          return relevantCondition.values
        })
      })
      return retval
    })

    const renderTable = () => {
      return (
        <Table
          columns={tableColumns}
          dataSource={dataSource}
          pagination={false}
          scroll={{
            x: "max-content",
          }}
        ></Table>
      )
    }

    const renderInfo = () => {
      if (displayType === "table") return renderTable()
      return renderList()
    }

    return (
      <div>
        {pagination}

        {networkOnly({
          status: API_USERS_DOCUMENTS_INDEX_STATUS,
          data: documents,
          render: renderInfo,
        })}

        {pagination}
      </div>
    )
  }

  const renderPageContent = () => {
    return (
      <StyledPageContent>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>{}</div>
          <div className="action-buttons top">
            <Tooltip title="Create a new record">
              <Button type="primary" ghost onClick={handleCreate}>
                Create Document
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="content">{renderContents()}</div>
      </StyledPageContent>
    )
  }

  const accessibleFeatures = globalStore.currentUser?.accessible_features || []

  if (
    accessibleFeatures?.length > 0 &&
    !accessibleFeatures.includes("fitment-data-view")
  )
    return <Redirect to="/" />

  return (
    <div className="page-content salary-ranges-page">
      <StyledBox>
        {staleWhileRevalidate({
          status: API_USERS_DOCUMENTS_INDEX_STATUS,
          data: documents,
          render: renderPageContent,
        })}
      </StyledBox>
    </div>
  )
}

export default view(DocumentIndexPage)
